<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import SplitButton from 'primevue/splitbutton';
import type { MenuItem } from 'primevue/menuitem';
import type { SplitButtonProps } from 'primevue/splitbutton';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import { useUtilities } from '@apparatix/composables';
import { AvailableViews } from '@apparatix/types';
import { Relationship } from '@apparatix/schemas/Relationship';
import { SupportedActivitiesView, useActivities } from '@apparatix/composables';
import type { ActivityTypeName } from '@apparatix/schemas/Activities';
import PlusIcon from '@primeicons/vue/plus';

type Props = {
  // eslint-disable-next-line vue/require-default-prop
  view?: AvailableViews;
  // eslint-disable-next-line vue/require-default-prop
  parentObject?: Relationship;
  // eslint-disable-next-line vue/require-default-prop
  buttonPtProps?: SplitButtonProps;
  types?: ActivityTypeName[];
  defaultType?: ActivityTypeName;
}

const props = withDefaults(defineProps<Props>(), {
  types: () => ['Note', 'Reminder', 'Task'],
  defaultType: 'Task',
});

const { addActivityComponent, showModal } = useCreateActivity();
const { getDisplayType, getIconForActivityType } = useActivities();
const { arrayHasValues } = useUtilities();

const menuItems = computed<MenuItem[]>(() => {
  const defaultType: ActivityTypeName = props.defaultType ?? 'Task';

  return props.types.filter(type => type !== defaultType).map(type => ({
    label: `Add ${getDisplayType(type).toLowerCase()}`,
    icon: getIconForActivityType(type),
    command: () => showModal(type, props.view as SupportedActivitiesView, props.parentObject),
  }));
});

const commonButtonProps = computed(() => ({
  label: `Add ${getDisplayType(arrayHasValues(props.types, { exactly: 1 }) ? props.types[0] : props.defaultType)}`,
  ...props.buttonPtProps,
}));

const onDefaultActionClick = (type: ActivityTypeName) => showModal(type, props.view as SupportedActivitiesView, props.parentObject);
</script>
<template>
  <Button
    v-if="arrayHasValues(props.types, {exactly: 1})"
    v-bind="commonButtonProps"
    @click="onDefaultActionClick(props.types[0])"
  >
    <PlusIcon />
    {{ commonButtonProps.label }}
  </Button>
  <SplitButton
    v-if="arrayHasValues(props.types, {min: 2})"
    v-bind="commonButtonProps"
    :model="menuItems"
    @click="onDefaultActionClick(props.defaultType)"
  >
    <template #icon>
      <PlusIcon />
    </template>
  </SplitButton>
  <component :is="addActivityComponent" />
</template>
